import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux/es/hooks/useSelector";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { API_ENDPOINT } from "../../../services/api/resources";
import classes from "../../TabStyles/DashboardTab.module.css";

const T = {
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  border: "rgba(226,232,240,0.76)",
  text: "#0F172A",
  textSec: "#334155",
  muted: "#64748B",
  brand: "#4F7CFF",
  info: "#0EA5E9",
  success: "#24C8A5",
  warning: "#F5A524",
  danger: "#EF4444",
  purple: "#7C3AED",
  teal: "#14B8A6",
  pink: "#DB2777",
  fontBody: "GilroyRegular",
  fontSemiBold: "GilroySemiBold",
  fontDisplay: "GilroyBold",
};

const STATUS_COLORS = ["#24C8A5", "#F5A524", "#EF4444", "#7C3AED", "#64748B"];
const FIELD_COLORS = ["#4F7CFF", "#24C8A5", "#F5A524", "#EF4444"];
const ALL_MONTHS = "all";

const toNumber = (value) => {
  const number = Number(String(value ?? 0).replace(/,/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const toPercent = (part, total) => {
  const base = toNumber(total);
  if (!base) return "0%";
  return `${Math.round((toNumber(part) / base) * 100)}%`;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(toNumber(value));

const formatCurrency = (value, currency = "INR") => {
  const amount = toNumber(value);
  if (amount >= 10000000) return `${currency} ${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `${currency} ${(amount / 100000).toFixed(2)} L`;
  return `${currency} ${formatNumber(amount)}`;
};

const titleCase = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const monthLabel = (row = {}) =>
  `${row.month_name || row.month || ""}${row.year ? ` ${String(row.year).slice(-2)}` : ""}`.trim();

const buildMonthRows = (rows = [], mapper = (row) => row) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    label: monthLabel(row),
    year: toNumber(row.year),
    month: toNumber(row.month),
    monthName: row.month_name || "",
    ...mapper(row),
  }));

const objectToRows = (source = {}, labelKey = "name", valueKey = "value") =>
  Object.entries(source || {}).map(([key, value]) => ({
    [labelKey]: titleCase(key),
    [valueKey]: toNumber(value),
    rawKey: key,
  }));

const topRows = (rows = [], valueKey = "value", limit = 8) =>
  [...rows].sort((a, b) => toNumber(b?.[valueKey]) - toNumber(a?.[valueKey])).slice(0, limit);

const tooltipTextStyle = {
  margin: 0,
  color: T.muted,
  fontFamily: T.fontBody,
  fontSize: 14,
  lineHeight: 1.45,
};

const selectStyle = {
  height: 34,
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  background: "#fff",
  color: T.textSec,
  fontFamily: T.fontSemiBold,
  fontSize: 14,
  padding: "0 10px",
  outline: "none",
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
  marginBottom: 20,
  alignItems: "stretch",
};

const wideSplitGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: 20,
  alignItems: "stretch",
};

const chartShellStyle = {
  minWidth: 0,
  border: "1px solid rgba(226,232,240,0.7)",
  borderRadius: 16,
  padding: 16,
  background: "linear-gradient(180deg, #FFFFFF 0%, #F9FBFF 100%)",
};

const SectionCard = ({ title, subtitle, children }) => (
  <section
    style={{
      background: "linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%)",
      border: `1px solid ${T.border}`,
      borderRadius: 18,
      boxShadow: "0px 14px 34px rgba(15,23,42,0.07)",
      padding: "24px",
      marginBottom: 22,
      minWidth: 0,
      overflow: "visible",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 20,
        paddingBottom: 14,
        borderBottom: "1px solid rgba(148,163,184,0.18)",
      }}
    >
      <div>
        <h2 style={{ margin: 0, fontSize: 19, fontFamily: T.fontDisplay, color: T.text, letterSpacing: 0 }}>
          {title}
        </h2>
        {subtitle ? <p style={{ ...tooltipTextStyle, marginTop: 4 }}>{subtitle}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

const ChartTitle = ({ title, note }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 14, fontFamily: T.fontDisplay, color: T.text }}>{title}</div>
    {note ? <div style={{ ...tooltipTextStyle, marginTop: 3 }}>{note}</div> : null}
  </div>
);

const TrendControls = ({ data = [], selectedYear, selectedMonth, onYearChange, onMonthChange }) => {
  const years = useMemo(
    () => [...new Set(data.map((row) => row.year).filter(Boolean))].sort((a, b) => a - b),
    [data],
  );
  const months = useMemo(() => {
    const monthRows = data.filter((row) => !selectedYear || row.year === selectedYear);
    return [...new Map(monthRows.map((row) => [row.month, row.monthName || row.label])).entries()]
      .filter(([month]) => month)
      .sort(([left], [right]) => left - right);
  }, [data, selectedYear]);

  useEffect(() => {
    if (!years.length || years.includes(selectedYear)) return;
    onYearChange(years[years.length - 1]);
  }, [onYearChange, selectedYear, years]);

  useEffect(() => {
    if (selectedMonth === ALL_MONTHS) return;
    if (months.some(([month]) => month === selectedMonth)) return;
    onMonthChange(ALL_MONTHS);
  }, [months, onMonthChange, selectedMonth]);

  if (!years.length) return null;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginBottom: 10,
        flexWrap: "wrap",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <select
        value={selectedYear || years[years.length - 1]}
        onChange={(event) => onYearChange(toNumber(event.target.value))}
        style={selectStyle}
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
      <select
        value={selectedMonth}
        onChange={(event) =>
          onMonthChange(event.target.value === ALL_MONTHS ? ALL_MONTHS : toNumber(event.target.value))
        }
        style={selectStyle}
      >
        <option value={ALL_MONTHS}>All Months</option>
        {months.map(([month, label]) => (
          <option key={month} value={month}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
};

const useTrendRows = (data = [], enabled = false) => {
  const years = useMemo(
    () => [...new Set(data.map((row) => row.year).filter(Boolean))].sort((a, b) => a - b),
    [data],
  );
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1] || "");
  const [selectedMonth, setSelectedMonth] = useState(ALL_MONTHS);

  useEffect(() => {
    if (!years.length) {
      setSelectedYear("");
      setSelectedMonth(ALL_MONTHS);
      return;
    }
    setSelectedYear((current) => (years.includes(current) ? current : years[years.length - 1]));
  }, [years]);

  const filteredRows = useMemo(() => {
    if (!enabled || !selectedYear) return data;
    return data.filter((row) => {
      if (row.year !== selectedYear) return false;
      return selectedMonth === ALL_MONTHS || row.month === selectedMonth;
    });
  }, [data, enabled, selectedMonth, selectedYear]);

  return {
    filteredRows,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
  };
};

const EmptyChart = ({ label = "No dashboard data available" }) => (
  <div
    style={{
      height: 260,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: T.muted,
      fontFamily: T.fontBody,
      fontSize: 14,
      border: "1px dashed rgba(148,163,184,0.35)",
      borderRadius: 16,
      background: "#F8FAFC",
    }}
  >
    {label}
  </div>
);

const CustomTooltip = ({ active, payload, label, formatter = formatNumber }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(226,232,240,0.92)",
        boxShadow: "0px 12px 30px rgba(15,23,42,0.12)",
        borderRadius: 12,
        padding: "10px 12px",
        minWidth: 170,
        maxWidth: 320,
      }}
    >
      <div style={{ color: T.muted, fontFamily: T.fontSemiBold, fontSize: 14, marginBottom: 6 }}>
        {label}
      </div>
      {payload.map((item) => (
        <div
          key={`${item.name}-${item.dataKey}`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            color: T.text,
            fontFamily: T.fontBody,
            fontSize: 14,
            marginTop: 4,
          }}
        >
          <span style={{ color: item.color || T.textSec }}>{item.name}</span>
          <strong style={{ fontFamily: T.fontDisplay }}>{formatter(item.value, item)}</strong>
        </div>
      ))}
    </div>
  );
};

const Legend = ({ rows = [], colors = STATUS_COLORS, valueKey = "value", formatter = formatNumber }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 14px", marginTop: 14 }}>
    {rows.map((row, index) => (
      <div key={row.name || row.label} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: 999,
            background: colors[index % colors.length],
          }}
        />
        <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14 }}>
          {row.name || row.label}: {formatter(row[valueKey])}
        </span>
      </div>
    ))}
  </div>
);

const DefaultMetricTooltip = ({ title, value, note }) => (
  <div style={{ display: "grid", gap: 9 }}>
    <div>
      <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {title}
      </div>
      <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 15, marginTop: 2 }}>
        {value} total
      </div>
      <div style={{ color: T.muted, fontFamily: T.fontBody, fontSize: 12, marginTop: 1 }}>
        Year-wise breakdown
      </div>
    </div>
    <div style={{ display: "grid", gap: 6 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 20,
          padding: "7px 9px",
          borderRadius: 10,
          background: "#F8FAFC",
        }}
      >
        <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 13 }}>All years</span>
        <span style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 13, whiteSpace: "nowrap" }}>{value}</span>
      </div>
      {note ? <div style={{ color: T.muted, fontFamily: T.fontBody, fontSize: 12 }}>{note}</div> : null}
    </div>
  </div>
);

const MetricCard = ({ title, value, note, accent = T.brand, tooltip, onClick, emphasis = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipContent = tooltip || <DefaultMetricTooltip title={title} value={value} note={note} />;
  const interactive = Boolean(onClick || tooltipContent);

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        onClick();
      }}
      style={{
        background: `linear-gradient(180deg, ${emphasis ? "rgba(20,184,166,0.08)" : "#FFFFFF"} 0%, #FFFFFF 72%)`,
        border: `1px solid ${emphasis ? "rgba(20,184,166,0.28)" : T.border}`,
        borderRadius: 16,
        minHeight: emphasis ? 142 : 118,
        padding: "20px 20px 18px",
        boxShadow: emphasis ? "0px 18px 42px rgba(15,23,42,0.12)" : "0px 10px 28px rgba(15,23,42,0.065)",
        position: "relative",
        overflow: "visible",
        cursor: interactive ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: "0 0 auto",
          height: 4,
          background: `linear-gradient(90deg, ${accent}, rgba(255,255,255,0.6))`,
          borderRadius: "16px 16px 0 0",
        }}
      />
      <div>
        <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 14, marginBottom: 10 }}>
          {title}
        </div>
        <div
          style={{
            color: accent,
            fontFamily: T.fontDisplay,
            fontSize: emphasis ? 27 : 23,
            lineHeight: 1.15,
            wordBreak: "break-word",
          }}
        >
          {value}
        </div>
      </div>
      {note ? (
        <div style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14, marginTop: 8 }}>
          {note}
        </div>
      ) : null}
      {tooltipContent && isOpen ? (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            left: 14,
            right: 14,
            top: "calc(100% + 8px)",
            background: "#fff",
            border: `1px solid ${T.border}`,
            boxShadow: "0px 16px 36px rgba(15,23,42,0.15)",
            borderRadius: 12,
            padding: "10px 12px",
          }}
        >
          {tooltipContent}
        </div>
      ) : null}
    </div>
  );
};

const BarPanel = ({ title, note, data, bars, height = 280, formatter = formatNumber, onClick, trendFilter = false }) => {
  const {
    filteredRows,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
  } = useTrendRows(data, trendFilter);

  return (
    <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <ChartTitle title={title} note={note} />
      {trendFilter ? (
        <TrendControls
          data={data}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
        />
      ) : null}
      {filteredRows.length ? (
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredRows} margin={{ top: 10, right: 14, left: 0, bottom: 36 }}>
              <CartesianGrid stroke="rgba(226,232,240,0.82)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={56} />
              <YAxis tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} width={54} tickFormatter={formatter} />
              <RechartsTooltip content={(props) => <CustomTooltip {...props} formatter={formatter} />} />
              {bars.map((bar) => (
                <Bar key={bar.key} dataKey={bar.key} name={bar.name} fill={bar.color} radius={[8, 8, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const LinePanel = ({ title, note, data, lines, height = 280, formatter = formatNumber, onClick, trendFilter = false }) => {
  const {
    filteredRows,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
  } = useTrendRows(data, trendFilter);

  return (
    <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <ChartTitle title={title} note={note} />
      {trendFilter ? (
        <TrendControls
          data={data}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
        />
      ) : null}
      {filteredRows.length ? (
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filteredRows} margin={{ top: 10, right: 16, left: 0, bottom: 36 }}>
              <CartesianGrid stroke="rgba(226,232,240,0.82)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={56} />
              <YAxis tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} width={54} tickFormatter={formatter} />
              <RechartsTooltip content={(props) => <CustomTooltip {...props} formatter={formatter} />} />
              {lines.map((line) => (
                <Line key={line.key} type="monotone" dataKey={line.key} name={line.name} stroke={line.color} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const DonutPanel = ({ title, note, data, height = 280, formatter = formatNumber, onClick }) => {
  const total = data.reduce((sum, row) => sum + toNumber(row.value), 0);

  return (
    <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <ChartTitle title={title} note={note} />
      {data.length ? (
        <>
          <div style={{ width: "100%", height }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="82%" paddingAngle={3}>
                  {data.map((entry, index) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      formatter={(value) => `${formatter(value)} (${toPercent(value, total)})`}
                    />
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <Legend rows={data} formatter={formatter} />
        </>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const HorizontalBarPanel = ({ title, note, data, valueKey = "value", height = 320, formatter = formatNumber, onClick }) => (
  <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
    <ChartTitle title={title} note={note} />
    {data.length ? (
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 6, right: 20, left: 20, bottom: 6 }}>
            <CartesianGrid stroke="rgba(226,232,240,0.82)" strokeDasharray="3 4" horizontal={false} />
            <XAxis type="number" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} tickFormatter={formatter} />
            <YAxis type="category" dataKey="label" width={132} tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} />
            <RechartsTooltip content={(props) => <CustomTooltip {...props} formatter={formatter} />} />
            <Bar dataKey={valueKey} name={title} fill={T.purple} radius={[0, 8, 8, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <EmptyChart />
    )}
  </div>
);

const InsightList = ({ rows = [], valueKey = "value", formatter = formatNumber }) => (
  <div style={{ display: "grid", gap: 10 }}>
    {rows.length ? rows.map((row, index) => (
      <div
        key={`${row.label}-${index}`}
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          padding: "11px 12px",
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          background: "#fff",
        }}
      >
        <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14, minWidth: 0 }}>
          {row.label}
        </span>
        <strong style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 14, whiteSpace: "nowrap" }}>
          {formatter(row[valueKey])}
        </strong>
      </div>
    )) : <EmptyChart label="No records available" />}
  </div>
);

const ControlTowerBanner = ({ currency, bidSummary, poSummary, invoiceSummary }) => {
  const selectedBidValue = toNumber(bidSummary?.selected_bid_value);
  const poBudget = toNumber(poSummary?.total_po_budget);
  const invoiceAmount = toNumber(invoiceSummary?.total_invoice_amount);
  const totalCommercialValue = selectedBidValue + poBudget + invoiceAmount;
  const healthRows = [
    { label: "Bid Win Rate", value: toPercent(bidSummary?.success_bid, bidSummary?.submitted_bid), color: T.success },
    { label: "Invoice Verified", value: toPercent(invoiceSummary?.verified_count, invoiceSummary?.total_invoice_count), color: T.info },
  ];

  return (
    <section
      style={{
        background: "linear-gradient(135deg, #FFFFFF 0%, #F2F7FF 58%, #ECFDF9 100%)",
        border: `1px solid ${T.border}`,
        borderRadius: 20,
        boxShadow: "0px 16px 40px rgba(15,23,42,0.08)",
        padding: "24px",
        marginBottom: 20,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 20,
        alignItems: "center",
      }}
    >
      <div>
        <div
          style={{
            display: "inline-flex",
            padding: "5px 10px",
            borderRadius: 999,
            background: "rgba(79,124,255,0.1)",
            color: T.brand,
            fontFamily: T.fontDisplay,
            fontSize: 14,
            marginBottom: 12,
          }}
        >
          Supplier Control Tower
        </div>
        <h1
          style={{
            margin: 0,
            color: T.text,
            fontFamily: T.fontDisplay,
            fontSize: 26,
            lineHeight: 1.2,
            letterSpacing: 0,
          }}
        >
          Commercial performance across bids, POs, invoices, and matching.
        </h1>
       
      </div>
      <div
        style={{
          background: "rgba(255,255,255,0.78)",
          border: "1px solid rgba(226,232,240,0.78)",
          borderRadius: 16,
          padding: 16,
          display: "grid",
          gap: 13,
        }}
      >
        <div>
          <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 14 }}>Commercial Value Tracked</div>
          <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 24, marginTop: 3 }}>
            {formatCurrency(totalCommercialValue, currency)}
          </div>
        </div>
        {healthRows.map((row) => (
          <div key={row.label}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
              <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14 }}>{row.label}</span>
              <strong style={{ color: row.color, fontFamily: T.fontDisplay, fontSize: 14 }}>{row.value}</strong>
            </div>
            <div style={{ height: 7, borderRadius: 999, background: "rgba(226,232,240,0.9)", overflow: "hidden" }}>
              <div
                style={{
                  width: row.value,
                  maxWidth: "100%",
                  height: "100%",
                  borderRadius: 999,
                  background: row.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const VendorAdminDashboardTab = () => {
  const data = useSelector((state) => state.loginData.value);
  const navigate = useNavigate();

  const authToken = data?.auth_token;
  const vendorToken = data?.user?.token;

  const [, setLoadingState] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const [vendorUserCount, setVendorUserCount] = useState(0);

  const postWithAuth = useCallback(
    (url, payload) =>
      axios({
        method: "POST",
        url,
        data: payload,
        headers: { Authorization: `Bearer ${authToken}` },
      }),
    [authToken],
  );

  const getAnalyticsValues = useCallback(async () => {
    if (!authToken || !vendorToken) return;

    const formData = new FormData();
    formData.append("vendor_token", vendorToken);

    setLoadingState(true);
    try {
      const response = await postWithAuth(API_ENDPOINT.new_vendor_dashbord, formData);
      setDashboardData(response?.data || {});
    } catch (error) {
      console.log("new_vendor_dashbord", error);
      setDashboardData({});
    } finally {
      setLoadingState(false);
    }
  }, [
    authToken,
    postWithAuth,
    vendorToken,
  ]);

  const getVendorUserCount = useCallback(async () => {
    if (!authToken || !vendorToken) return;
    const formData = new FormData();
    formData.append("page", 1);
    formData.append("vendor_token", vendorToken);
    try {
      const response = await postWithAuth(API_ENDPOINT.get_vendor_contacts, formData);
      setVendorUserCount(
        toNumber(
          response?.data?.total_count ||
            response?.data?.total_items ||
            response?.data?.count ||
            response?.data?.results?.length,
        ),
      );
    } catch (error) {
      console.log("get_vendor_contacts", error);
      setVendorUserCount(0);
    }
  }, [authToken, postWithAuth, vendorToken]);

  useEffect(() => {
    getVendorUserCount();
  }, [getVendorUserCount]);

  useEffect(() => {
    if (!authToken || !vendorToken) return;
    getAnalyticsValues();
  }, [authToken, getAnalyticsValues, vendorToken]);

  const bid = dashboardData?.bid_submission || {};
  const bidSummary = bid?.summary || {};
  const po = dashboardData?.customer_po || {};
  const poSummary = po?.summary || {};
  const invoice = dashboardData?.invoice_reconciliation || {};
  const invoiceSummary = invoice?.summary || {};

  const currency =
    bidSummary.currency ||
    poSummary.currency ||
    invoiceSummary.currency ||
    "INR";

  const bidMonthRows = useMemo(
    () =>
      buildMonthRows(bid?.monthwise, (row) => ({
        total: toNumber(row.total_bid),
        submitted: toNumber(row.submitted_bid),
        success: toNumber(row.success_bid),
        notSubmitted: toNumber(row.not_submitted_bid),
      })),
    [bid?.monthwise],
  );

  const bidYearRows = useMemo(
    () =>
      (Array.isArray(bid?.yearwise) ? bid.yearwise : []).map((row) => ({
        label: row.year || "Unknown",
        total: toNumber(row.total_bid),
        submitted: toNumber(row.submitted_bid),
        success: toNumber(row.success_bid),
      })),
    [bid?.yearwise],
  );

  const poMonthRows = useMemo(
    () =>
      buildMonthRows(po?.monthwise, (row) => ({
        count: toNumber(row.po_count),
        budget: toNumber(row.po_budget),
      })),
    [po?.monthwise],
  );

  const invoiceMonthRows = useMemo(
    () =>
      buildMonthRows(invoice?.monthwise, (row) => ({
        count: toNumber(row.invoice_count),
        amount: toNumber(row.invoice_amount),
        matched: toNumber(row.matched_count),
        partial: toNumber(row.partially_matched_count),
        unverified: toNumber(row.unverified_count),
      })),
    [invoice?.monthwise],
  );

  const itemSpendRows = useMemo(
    () =>
      topRows(
        (Array.isArray(po?.itemwise_spend) ? po.itemwise_spend : []).map((row) => ({
          label: row.item_name || row.item_code || "Unknown item",
          spend: toNumber(row.spend),
          quantity: toNumber(row.quantity),
          poCount: toNumber(row.po_count),
        })),
        "spend",
      ),
    [po?.itemwise_spend],
  );

  const matchStatusRows = useMemo(
    () => objectToRows(invoice?.match_status_counts, "name", "value"),
    [invoice?.match_status_counts],
  );

  const approvalStatusRows = useMemo(
    () => objectToRows(invoice?.approval_status_counts, "name", "value"),
    [invoice?.approval_status_counts],
  );

  const buyerAmountRows = useMemo(
    () => topRows(objectToRows(invoice?.buyer_invoice_amounts, "label", "value"), "value", 5),
    [invoice?.buyer_invoice_amounts],
  );

  const fieldVerificationRows = useMemo(
    () =>
      Object.entries(invoice?.verification_field_counts || {}).map(([key, value]) => ({
        label: titleCase(key).replace("Po", "PO"),
        matched: toNumber(value?.matched),
        partial: toNumber(value?.partial),
        mismatch: toNumber(value?.mismatch),
        unknown: toNumber(value?.unknown),
      })),
    [invoice?.verification_field_counts],
  );

  const metrics = [
    {
      title: "Total Bids",
      value: formatNumber(bidSummary.total_bid),
      note: `${formatNumber(bidSummary.submitted_bid)} submitted`,
      accent: T.brand,
      onClick: () => navigate("/Vendor_dashboard/RFQ"),
      tooltip: (
        <div style={{ display: "grid", gap: 7 }}>
          <p style={tooltipTextStyle}>
            Submitted: {formatNumber(bidSummary.submitted_bid)}. Not submitted:{" "}
            {formatNumber(bidSummary.not_submitted_bid)}.
          </p>
          {bidYearRows.length ? (
            <div style={{ display: "grid", gap: 5 }}>
              {bidYearRows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    color: T.textSec,
                    fontFamily: T.fontSemiBold,
                    fontSize: 14,
                  }}
                >
                  <span>{row.label}</span>
                  <strong style={{ fontFamily: T.fontDisplay, color: T.text }}>
                    {formatNumber(row.total)} bids
                  </strong>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Successful Bids",
      value: formatNumber(bidSummary.success_bid),
      note: `${toPercent(bidSummary.success_bid, bidSummary.submitted_bid)} success rate`,
      accent: T.success,
      onClick: () => navigate("/Vendor_dashboard/RFQ"),
    },
    {
      title: "Selected Bid Value",
      value: formatCurrency(bidSummary.selected_bid_value, currency),
      note: "Awarded sourcing value",
      accent: T.teal,
      emphasis: true,
      onClick: () => navigate("/Vendor_dashboard/RFQ"),
    },
    {
      title: "Customer POs",
      value: formatNumber(poSummary.total_po_count),
      note: formatCurrency(poSummary.total_po_budget, currency),
      accent: T.purple,
      onClick: () => navigate("/Vendor_dashboard/PO"),
    },
    {
      title: "Invoices",
      value: formatNumber(invoiceSummary.total_invoice_count),
      note: `${formatNumber(invoiceSummary.verified_count)} verified`,
      accent: T.info,
      onClick: () => navigate("/Vendor_dashboard/InvoiceProcess"),
    },
    {
      title: "Supplier Users",
      value: formatNumber(vendorUserCount),
      note: "Active contact users",
      accent: T.pink,
      onClick: () => navigate("/Vendor_dashboard/Users"),
    },
  ];

  return (
    <div
      className={classes.dashboardPage}
      style={{ background: T.bg, fontFamily: T.fontBody, fontSize: 14 }}
    >
      <div className={classes.pageSectionPad}>
        <HeaderTitle titleText="Supplier Admin Dashboard" />
      </div>

      <div className={classes.pageSectionPadMain}>
        <div className={classes.dashboardGroup}>
          <ControlTowerBanner
            currency={currency}
            bidSummary={bidSummary}
            poSummary={poSummary}
            invoiceSummary={invoiceSummary}
          />

          <div style={metricGridStyle}>
            {metrics.map((metric) => (
              <MetricCard key={metric.title} {...metric} />
            ))}
          </div>

          <SectionCard
            title="Bid Submission Intelligence"
            subtitle="Sourcing participation, submitted bids, and award conversion."
          >
            <div style={wideSplitGridStyle}>
              <div style={chartShellStyle}>
                <BarPanel
                  title="Monthly Bid Performance"
                  note="Total, submitted, and successful bids from the new vendor dashboard API."
                  data={bidMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/RFQ")}
                  trendFilter={true}
                  bars={[
                    { key: "total", name: "Total Bids", color: T.brand },
                    { key: "submitted", name: "Submitted", color: T.info },
                    { key: "success", name: "Successful", color: T.success },
                  ]}
                />
              </div>
              <div style={chartShellStyle}>
                <DonutPanel
                  title="Bid Outcome Split"
                  note="Submitted, not submitted, successful, and not successful bid counts."
                  data={[
                    { name: "Submitted", value: bidSummary.submitted_bid },
                    { name: "Not Submitted", value: bidSummary.not_submitted_bid },
                    { name: "Successful", value: bidSummary.success_bid },
                    { name: "Not Successful", value: bidSummary.not_success_bid },
                  ].filter((row) => toNumber(row.value) > 0)}
                  onClick={() => navigate("/Vendor_dashboard/RFQ")}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Customer PO Analytics"
            subtitle="Customer purchase order volume, value, and item-level spend concentration."
          >
            <div style={wideSplitGridStyle}>
              <div style={chartShellStyle}>
                <LinePanel
                  title="PO Count Trend"
                  note="Monthly customer PO count."
                  data={poMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/PO")}
                  trendFilter={true}
                  lines={[{ key: "count", name: "PO Count", color: T.purple }]}
                />
              </div>
              <div style={chartShellStyle}>
                <BarPanel
                  title="PO Budget Trend"
                  note="Monthly PO budget value."
                  data={poMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/PO")}
                  formatter={(value) => formatCurrency(value, currency)}
                  trendFilter={true}
                  bars={[{ key: "budget", name: "PO Budget", color: T.teal }]}
                />
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <div style={chartShellStyle}>
                <HorizontalBarPanel
                  title="Top Items by PO Spend"
                  note="Highest value items, limited to keep the view readable."
                  data={itemSpendRows}
                  valueKey="spend"
                  formatter={(value) => formatCurrency(value, currency)}
                  onClick={() => navigate("/Vendor_dashboard/PO")}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Invoice Reconciliation"
            subtitle="Invoice verification, matching quality, and buyer-wise invoice exposure."
          >
            <div style={wideSplitGridStyle}>
              <div style={chartShellStyle}>
                <DonutPanel
                  title="Match Status"
                  note="Matched, partially matched, mismatched, and unverified invoices."
                  data={matchStatusRows.filter((row) => row.value > 0)}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                />
              </div>
              <div style={chartShellStyle}>
                <BarPanel
                  title="Verification Field Health"
                  note="Field-level match confidence from invoice reconciliation."
                  data={fieldVerificationRows}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                  bars={[
                    { key: "matched", name: "Matched", color: FIELD_COLORS[0] },
                    { key: "partial", name: "Partial", color: FIELD_COLORS[1] },
                    { key: "mismatch", name: "Mismatch", color: FIELD_COLORS[2] },
                    { key: "unknown", name: "Unknown", color: FIELD_COLORS[3] },
                  ]}
                />
              </div>
            </div>
            <div style={{ ...wideSplitGridStyle, marginTop: 20 }}>
              <div style={chartShellStyle}>
                <LinePanel
                  title="Invoice Amount Trend"
                  note="Monthly invoice value and verified match count."
                  data={invoiceMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                  formatter={(value, item) =>
                    item?.dataKey === "amount" ? formatCurrency(value, currency) : formatNumber(value)
                  }
                  trendFilter={true}
                  lines={[
                    { key: "amount", name: "Invoice Amount", color: T.info },
                    { key: "matched", name: "Matched Count", color: T.success },
                  ]}
                />
              </div>
              <div style={chartShellStyle}>
                <ChartTitle title="Buyer Invoice Exposure" note="Top invoice amount contributors." />
                <InsightList rows={buyerAmountRows} formatter={(value) => formatCurrency(value, currency)} />
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <div style={chartShellStyle}>
                <DonutPanel
                  title="Approval Status"
                  note="Invoice approval pipeline status."
                  data={approvalStatusRows.filter((row) => row.value > 0)}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                />
              </div>
            </div>
          </SectionCard>

        </div>
      </div>
    </div>
  );
};

export default VendorAdminDashboardTab;
